import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { CellIdentityLocation } from "@/utils/ixd-cell-model";
import type { CellNotice } from "../cell-linkage-floating-panel";

/**
 * 셀 위에 표시할 알림. 의미는 소비하는 화면이 정하고, 렌더러는 표시만 한다 —
 * 렌더러가 특정 화면의 도메인을 알면 결합이 양방향이 된다.
 *
 * 팝오버가 그리는 타입을 그대로 쓴다. 여기서 따로 정의하면 두 모양이 갈린다.
 */
export type IxdCellNotice = CellNotice;

interface IxdPreviewConfig {
  editable: boolean;
  /** identity key → 셀 알림 */
  cellNotices?: ReadonlyMap<string, IxdCellNotice>;
  identityLocationsByKey?: Map<string, CellIdentityLocation[]>;
  /** 셀 색·모서리 표식의 뜻을 셀 하단에 한 줄로 적을지 — IXD 값 편집기에서만 켠다 */
  showCellFootnotes?: boolean;
  showRawValueInEdit?: boolean;
  onCellEditStart?: () => void;
  onCellMouseDown?: (cellId: string, event: React.MouseEvent) => void;
  onCellMouseEnter?: (event: React.MouseEvent) => void;
  onCellMouseUp?: () => void;
}

const DEFAULT_CONFIG: IxdPreviewConfig = { editable: false };

const IxdPreviewConfigContext = createContext<IxdPreviewConfig>(DEFAULT_CONFIG);

export const IxdPreviewConfigProvider = ({
  children,
  editable,
  cellNotices,
  identityLocationsByKey,
  showCellFootnotes,
  showRawValueInEdit,
  onCellEditStart,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
}: IxdPreviewConfig & { children: ReactNode }) => {
  const value = useMemo<IxdPreviewConfig>(
    () => ({
      editable,
      cellNotices,
      identityLocationsByKey,
      showCellFootnotes,
      showRawValueInEdit,
      onCellEditStart,
      onCellMouseDown,
      onCellMouseEnter,
      onCellMouseUp,
    }),
    [
      editable,
      cellNotices,
      identityLocationsByKey,
      showCellFootnotes,
      showRawValueInEdit,
      onCellEditStart,
      onCellMouseDown,
      onCellMouseEnter,
      onCellMouseUp,
    ],
  );
  return (
    <IxdPreviewConfigContext.Provider value={value}>
      {children}
    </IxdPreviewConfigContext.Provider>
  );
};

export const useIxdPreviewConfig = (): IxdPreviewConfig =>
  useContext(IxdPreviewConfigContext);
